import type { EmailAttachment } from "@productlane/zero/types"

import type { FormattedAttachment } from "../hooks/useAttachments"

/**
 * Converts raw attachments (objects or JSON strings) into a consistent
 * FormattedAttachment array.
 *
 * - If an attachment has a `url`, it is transformed into a FormattedAttachment.
 * - If it already has `Content`, it is returned as-is.
 * - Invalid or unparsable items are skipped.
 */
export function formatAttachments(
	attachments: unknown[],
): FormattedAttachment[] {
	return attachments
		.map((attachment) => {
			if (!attachment) {
				return null
			}

			let parsed: unknown = attachment

			if (typeof attachment === "string") {
				try {
					parsed = JSON.parse(attachment)
				} catch {
					return null
				}
			}

			if (!parsed || typeof parsed !== "object") {
				return null
			}

			const hasUrl = Object.prototype.hasOwnProperty.call(parsed, "url")
			const hasContent = Object.prototype.hasOwnProperty.call(parsed, "Content")

			if (hasUrl && !hasContent) {
				const emailAttachment = parsed as EmailAttachment
				return {
					Content: emailAttachment.url,
					Name: emailAttachment.name,
					ContentType: emailAttachment.contentType,
					ContentLength: emailAttachment.size,
					ContentID: emailAttachment.id,
				} as FormattedAttachment
			}
			if (hasContent) {
				return parsed as FormattedAttachment
			}
			return null
		})
		.filter(
			(attachment): attachment is FormattedAttachment => attachment !== null,
		)
}
